import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A custom element that renders a SPH slider using slideshow logic.
 *
 * @typedef {object} Refs
 * @property {import('./slideshow').Slideshow} slideshow - The slideshow component.
 * @property {HTMLDialogElement} [dialog] - The dialog element.
 * @property {HTMLElement[]} [slides] - The slide elements.
 * @property {HTMLElement[]} [thumbnails] - The thumbnail elements.
 * @property {HTMLButtonElement} [closeButton] - The close button element.
 *
 * @extends Component<Refs>
 */
export class SPHSlider extends Component {
  requiredRefs = ['slideshow'];

  /** @type {{x:number,y:number,moved:boolean,pointerType:string,pointerId:number} | null} */
  #backdropPointer = null;
  #backdropClosing = false;

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('slideshow:select', this.#handleSlideshowSelect);
    this.addEventListener('slideshow:transition-start', this.#handleTransitionStart);
    this.addEventListener('slideshow:transition-end', this.#handleTransitionEnd);
    this.addEventListener('drag-zoom:change', this.#handleZoomChange);

    // Capture backdrop taps before image wrappers stop click propagation.
    this.addEventListener('pointerdown', this.#trackBackdropPointer, true);
    this.addEventListener('pointermove', this.#trackBackdropMove, true);
    this.addEventListener('pointercancel', this.#cancelBackdropPointer, true);
    this.addEventListener('pointerup', this.#handleBackdropPointerUp, true);
    this.addEventListener('click', this.#handleBackdropClick, true);

    // Add keyboard support
    this.addEventListener('keydown', this.handleKeyDown);

    // Add close button click handler
    if (this.refs.closeButton) {
      this.refs.closeButton.addEventListener('click', () => this.close());
    }

    // Add background click to close
    this.addEventListener('click', this.#handleBackgroundClick);

    // Dialog background click: close only when not zoomed
    this.refs.dialog?.addEventListener('click', this.#handleDialogClick);

    // Hide slider by default only if not using dialog
    if (!this.refs.dialog) {
      this.style.display = 'none';
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('slideshow:select', this.#handleSlideshowSelect);
    this.removeEventListener('slideshow:transition-start', this.#handleTransitionStart);
    this.removeEventListener('slideshow:transition-end', this.#handleTransitionEnd);
    this.removeEventListener('drag-zoom:change', this.#handleZoomChange);
    this.removeEventListener('click', this.#handleBackgroundClick);
    this.refs.dialog?.removeEventListener('click', this.#handleDialogClick);

    this.removeEventListener('pointerdown', this.#trackBackdropPointer, true);
    this.removeEventListener('pointermove', this.#trackBackdropMove, true);
    this.removeEventListener('pointercancel', this.#cancelBackdropPointer, true);
    this.removeEventListener('pointerup', this.#handleBackdropPointerUp, true);
    this.removeEventListener('click', this.#handleBackdropClick, true);

    // Remove close button click handler
    if (this.refs.closeButton) {
      this.refs.closeButton.removeEventListener('click', () => this.close());
    }
  }

  /**
   * Opens the SPH slider to a specific slide.
   *
   * @param {number} index - The index of the slide to show.
   * @param {PointerEvent} [event] - The pointer event.
   */
  async open(index, event) {
    this.#backdropClosing = false;
    this.#backdropPointer = null;
    if (event) {
      event.preventDefault();
    }

    const { slideshow, dialog } = this.refs;

    if (!slideshow) return;

    const targetSlide = slideshow.slides?.[index];

    const openDialog = () => {
      if (dialog && dialog.showModal) {
        dialog.showModal();
      } else {
        this.style.display = 'block';
      }

      // Navigate to the specified slide
      slideshow.select(index, event, { animate: !prefersReducedMotion() });

      // Load high-resolution image for the current slide
      this.#loadHighResolutionImageForSlide(index);

      // Preload adjacent slides
      this.#preloadAdjacentSlides(index);
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions || !sourceImage || !targetSlide) return openDialog();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      openDialog();
      sourceImage.style.removeProperty('view-transition-name');
      targetSlide.style.setProperty('view-transition-name', transitionName);
    });

    targetSlide.style.removeProperty('view-transition-name');

    // Dispatch open event
    this.dispatchEvent(
      new CustomEvent('sph-slider:open', {
        detail: { index },
        bubbles: true,
      })
    );
  }

  /**
   * Closes the SPH slider.
   */
  async close() {
    const { dialog, slideshow } = this.refs;

    if (!supportsViewTransitions) return this.closeDialog();

    // Get the current slide index and element
    const currentIndex = this.currentIndex;
    const currentSlide = slideshow?.slides?.[currentIndex];

    if (!currentSlide) return this.closeDialog();

    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive
      ? mediaGallery.slideshow?.slides?.[currentIndex]
      : mediaGallery?.media?.[currentIndex];

    if (!slide) return this.closeDialog();

    if (dialog) {
      dialog.classList.add('dialog--closed');
      await onAnimationEnd(dialog);
    }

    currentSlide.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      currentSlide.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    if (dialog) {
      dialog.classList.remove('dialog--closed');
    }
  }

  closeDialog() {
    const { dialog } = this.refs;

    // Reset all zoom wrappers before closing (state persists across dialog opens)
    this.querySelectorAll('drag-zoom-wrapper').forEach((/** @type {any} */ wrapper) => wrapper.zoomOut?.());
    this.#updateZoomButton(false);

    if (dialog && dialog.close) {
      dialog.close();
    } else {
      this.style.display = 'none';
    }

    document.body.style.overflow = ''; // Restore background scrolling
    window.dispatchEvent(new DialogCloseEvent());

    // Dispatch close event
    this.dispatchEvent(
      new CustomEvent('sph-slider:close', {
        bubbles: true,
      })
    );
  }

  /**
   * Navigates to the next slide.
   */
  next() {
    this.refs.slideshow?.next();
  }

  /**
   * Navigates to the previous slide.
   */
  previous() {
    this.refs.slideshow?.previous();
  }

  /**
   * Navigates to a specific slide by index.
   * @param {number} index - The slide index.
   */
  goToSlide(index) {
    this.refs.slideshow?.select(index);
  }

  /**
   * Gets the current slide index.
   * @returns {number} The current slide index.
   */
  get currentIndex() {
    return this.refs.slideshow?.current || 0;
  }

  /**
   * Gets the total number of slides.
   * @returns {number} The total number of slides.
   */
  get slideCount() {
    return this.refs.slideshow?.slides?.length || 0;
  }

  /**
   * Loads a high-resolution image for a specific slide index
   * @param {number} slideIndex - The slide index
   */
  #loadHighResolutionImageForSlide(slideIndex) {
    const slide = this.refs.slideshow?.slides?.[slideIndex];
    if (!slide) return;

    const mediaContainer = slide.querySelector('.product-media-container--image');
    if (!mediaContainer || !(mediaContainer instanceof HTMLElement)) return;

    this.#loadHighResolutionImage(mediaContainer);
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Preloads images for adjacent slides to improve performance
   * @param {number} currentIndex - The current slide index
   */
  #preloadAdjacentSlides(currentIndex) {
    const { slideshow } = this.refs;
    if (!slideshow?.slides) return;

    // Preload previous and next slides
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : slideshow.slides.length - 1;
    const nextIndex = currentIndex < slideshow.slides.length - 1 ? currentIndex + 1 : 0;

    this.#loadHighResolutionImageForSlide(prevIndex);
    this.#loadHighResolutionImageForSlide(nextIndex);
  }

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    const slideIndex = event.detail.index;

    // Reset zoom on all wrappers when navigating to a new slide
    this.querySelectorAll('drag-zoom-wrapper').forEach((/** @type {any} */ wrapper) => wrapper.zoomOut?.());
    this.#updateZoomButton(false);

    // Load high-resolution image for the selected slide
    this.#loadHighResolutionImageForSlide(slideIndex);

    // Preload adjacent slides
    this.#preloadAdjacentSlides(slideIndex);

    // Update thumbnail selection if thumbnails exist
    this.#updateThumbnailSelection(slideIndex);

    // Dispatch a custom event for external listeners
    this.dispatchEvent(
      new CustomEvent('sph-slider:slide-change', {
        detail: {
          index: slideIndex,
          slide: this.refs.slideshow?.slides?.[slideIndex],
        },
        bubbles: true,
      })
    );
  };

  /**
   * Handles the transition start event.
   * @param {CustomEvent} event - The transition start event.
   */
  #handleTransitionStart = (event) => {
    this.dispatchEvent(
      new CustomEvent('sph-slider:transition-start', {
        detail: event.detail,
        bubbles: true,
      })
    );
  };

  /**
   * Handles the transition end event.
   * @param {CustomEvent} event - The transition end event.
   */
  #handleTransitionEnd = (event) => {
    this.dispatchEvent(
      new CustomEvent('sph-slider:transition-end', {
        detail: event.detail,
        bubbles: true,
      })
    );
  };


  #trackBackdropPointer = (event) => {
    if (!event.isPrimary) {
      if (this.#backdropPointer) this.#backdropPointer.moved = true;
      return;
    }
    this.#backdropPointer = {
      x: event.clientX, y: event.clientY, moved: false,
      pointerType: event.pointerType, pointerId: event.pointerId,
    };
  };

  #cancelBackdropPointer = () => {
    this.#backdropPointer = null;
  };

  #trackBackdropMove = (event) => {
    const start = this.#backdropPointer;
    if (start && (event.pointerId !== start.pointerId ||
        Math.hypot(event.clientX - start.x, event.clientY - start.y) > 6)) {
      start.moved = true;
    }
  };

  #handleBackdropPointerUp = (event) => {
    if (!['touch', 'pen'].includes(event.pointerType) ||
        event.pointerId !== this.#backdropPointer?.pointerId) return;
    this.#handleBackdropClick(event);
    this.#backdropPointer = null;
  };

  /** Close on a backdrop click or tap, including contain-image letterboxing.
   * Toolbar, thumbnails, arrows, image pixels and drag/pan gestures stay interactive.
   * @param {MouseEvent | PointerEvent} event
   */
  #handleBackdropClick = (event) => {
    if (this.#backdropClosing) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (!this.hasAttribute('data-sph-explicit-zoom') ||
        !this.refs.dialog?.open || (event.detail === 0 && event.type !== 'pointerup')) return;
    const start = this.#backdropPointer;
    if (!start || !['mouse', 'touch', 'pen'].includes(start.pointerType) || start.moved ||
        Math.hypot(event.clientX - start.x, event.clientY - start.y) > 6) return;
    const target = event.target;
    if (!(target instanceof Element) ||
        target.closest('button, a, slideshow-controls, video, deferred-media, product-model, model-viewer')) return;
    const wrapper = this.#getActiveZoomWrapper();
    const image = wrapper?.querySelector('img');
    if (!image || !image.naturalWidth || !image.naturalHeight) return;
    // getBoundingClientRect includes the current zoom/pan transform.
    const rect = image.getBoundingClientRect();
    const ratio = image.naturalWidth / image.naturalHeight;
    const width = Math.min(rect.width, rect.height * ratio);
    const height = Math.min(rect.height, rect.width / ratio);
    const left = rect.left + (rect.width - width) / 2;
    const top = rect.top + (rect.height - height) / 2;
    const stage = wrapper.getBoundingClientRect();
    const inside = event.clientX >= Math.max(left, stage.left) &&
      event.clientX <= Math.min(left + width, stage.right) &&
      event.clientY >= Math.max(top, stage.top) &&
      event.clientY <= Math.min(top + height, stage.bottom);
    if (inside) return;
    this.#backdropClosing = true;
    event.preventDefault();
    event.stopPropagation();
    this.#backdropPointer = null;
    this.close();
  };

  /**
   * Handles background click to close the slider.
   * @param {MouseEvent} event - The click event.
   */
  #handleBackgroundClick = (event) => {
    // Only close if clicking on the background (the slider itself), not on content
    if (event.target === this) {
      this.close();
    }
  };

  /**
   * Handles click on the dialog element to close it when clicking the background.
   * Replaces the inline onclick="this.close()" to prevent accidental close when zoomed.
   * @param {MouseEvent} event - The click event.
   */
  #handleDialogClick = (event) => {
    // Only close when clicking the dialog background, not content bubbling up
    if (event.target !== this.refs.dialog) return;
    // Don't close while zoomed in — prevents accidental close during pan gesture
    if (this.#getActiveZoomWrapper()?.isZoomed) return;
    this.close();
  };

  /**
   * Toggles zoom in/out on the currently visible drag-zoom-wrapper.
   */
  handleZoomToggle() {
    const wrapper = this.#getActiveZoomWrapper();
    if (!wrapper) return;
    wrapper.isZoomed ? wrapper.zoomOut() : wrapper.zoomIn();
  }

  /**
   * Returns the drag-zoom-wrapper for the currently active slide.
   * @returns {any | null}
   */
  #getActiveZoomWrapper() {
    const slide = this.refs.slideshow?.slides?.[this.currentIndex];
    return /** @type {any} */ (slide?.querySelector('drag-zoom-wrapper')) ?? null;
  }

  /**
   * Handles zoom state changes from drag-zoom-wrapper.
   * @param {CustomEvent} event
   */
  #handleZoomChange = (event) => {
    const wrapper = /** @type {HTMLElement} */ (event.target).closest('drag-zoom-wrapper');
    const isZoomed = /** @type {any} */ (wrapper)?.isZoomed ?? false;
    this.#updateZoomButton(isZoomed);
  };

  /**
   * Updates the zoom button icon and aria-label.
   * @param {boolean} isZoomed
   */
  #updateZoomButton(isZoomed) {
    const { zoomButton } = this.refs;
    if (!zoomButton) return;
    zoomButton.classList.toggle('zoom-dialog__zoom-button--zoomed', isZoomed);
    const zoomInLabel = zoomButton.dataset.sphZoomInLabel || 'Zoom';
    const zoomOutLabel = zoomButton.dataset.sphZoomOutLabel || 'Zoom out';
    zoomButton.setAttribute('aria-label', isZoomed ? zoomOutLabel : zoomInLabel);
  }

  /**
   * Updates the thumbnail selection to match the current slide.
   * @param {number} activeIndex - The active slide index.
   */
  #updateThumbnailSelection(activeIndex) {
    const thumbnails = this.querySelectorAll('.slideshow-controls button');

    thumbnails.forEach((button, index) => {
      const isActive = index === activeIndex;
      button.setAttribute('aria-selected', String(isActive));
      button.classList.toggle('active', isActive);
    });
  }

  /**
   * Handles keyboard navigation.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case 'Home':
        event.preventDefault();
        this.goToSlide(0);
        break;
      case 'End':
        event.preventDefault();
        this.goToSlide(this.slideCount - 1);
        break;
    }
  }

  /**
   * Handles thumbnail click events.
   * @param {number} index - The index of the thumbnail clicked.
   */
  handleThumbnailClick(index) {
    this.goToSlide(index);
  }

  /**
   * Handles thumbnail pointer enter events for preloading.
   * @param {number} index - The index of the thumbnail hovered.
   */
  handleThumbnailPointerEnter(index) {
    this.#loadHighResolutionImageForSlide(index);
  }
}

if (!customElements.get('sph-slider')) {
  customElements.define('sph-slider', SPHSlider);
}

